import { PencilIcon } from '@village/icons';
import { Button, Text } from '@village/ui';
import { useBooking } from 'hooks';
import { FC, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { formatDate, isLegalAge } from 'utils/date';
import { formatPhoneNumber } from 'utils/phone-number';

import * as Styled from './styles';

const PatientInformationCard: FC = () => {
    const navigate = useNavigate();
    const { search } = useLocation();
    const { booking } = useBooking();
    const { patientInformation } = booking;

    const goToPatientDetailForm = useCallback(() => {
        navigate({ pathname: '/patient-information', search });
    }, [navigate, search]);

    if (!patientInformation) {
        return null;
    }

    const legalAge = isLegalAge(patientInformation.dateOfBirth);

    return (
        <Styled.Container $borders={false}>
            <Styled.Header>
                <Styled.Title type="sub3">Patient details</Styled.Title>
                <Button
                    size="small"
                    variant="link"
                    onClick={goToPatientDetailForm}
                    data-testid="edit-patient-information"
                    startIcon={<PencilIcon />}
                >
                    Edit
                </Button>
            </Styled.Header>
            <Styled.Wrapper>
                <Styled.InfoGroup>
                    <Text type="h6">Patient name</Text>
                    <Text type="body1" data-testid="patient-name">
                        {patientInformation.firstName} {patientInformation.lastName}
                    </Text>
                </Styled.InfoGroup>
                <Styled.InfoGroup>
                    <Text type="h6">Date of Birth</Text>
                    <Text type="body1" data-testid="patient-dob">
                        {formatDate(patientInformation.dateOfBirth, 'monthDayYear')}
                    </Text>
                </Styled.InfoGroup>
                <Styled.InfoGroup>
                    <Text type="h6">Email</Text>
                    <Text type="body1" data-testid="patient-email">
                        {patientInformation.email}
                    </Text>
                </Styled.InfoGroup>
                <Styled.InfoGroup>
                    <Text type="h6">Contact number</Text>
                    <Text type="body1" data-testid="patient-contact-number">
                        {formatPhoneNumber(patientInformation.phoneNumber)}
                    </Text>
                </Styled.InfoGroup>
                <Styled.InfoGroup>
                    <Text type="h6">Sex</Text>
                    <Text type="body1" data-testid="patient-sex">
                        {patientInformation.sex === 'F' ? 'Female' : 'Male'}
                    </Text>
                </Styled.InfoGroup>
                {patientInformation.phoneType ? (
                    <Styled.InfoGroup>
                        <Text type="h6">Phone type</Text>
                        <Text type="body1" data-testid="patient-phone-type">
                            {patientInformation.phoneType}
                        </Text>
                    </Styled.InfoGroup>
                ) : null}
                <Styled.InfoGroup>
                    <Text type="h6">Home address</Text>
                    <Text type="body1" data-testid="patient-address1">
                        {patientInformation.address} {patientInformation.addressCount}
                    </Text>
                    <Text type="body1" data-testid="patient-address2">
                        {patientInformation.city}, {patientInformation.state}, {patientInformation.zip}
                    </Text>
                </Styled.InfoGroup>
                {!legalAge ? (
                    <Styled.InfoGroup>
                        <Text type="caption1">Patient has certified parent or legal guardian</Text>
                    </Styled.InfoGroup>
                ) : null}
            </Styled.Wrapper>
        </Styled.Container>
    );
};

export { PatientInformationCard };
